// @todo: Migrate to 'Dialog.Drawer' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import Drawer from '@material-ui/core/Drawer';
import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import {Option} from '@wandb/weave/common/util/uihelpers';
// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
// @todo: Migrate to 'PotentialRoadmap' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Loading} from '@wandb/weave/components/Loading';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import * as String from '@wandb/weave/core/util/string';
import _ from 'lodash';
import React, {memo, useCallback, useContext, useState} from 'react';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from 'react-toastify';

import {
  Entity,
  OrganizationMembersTableQuery,
  OrganizationSubscriptionInfoQuery,
  useAddUserToOrganizationMutation,
  useCreateInviteMutation,
} from '../generated/graphql';
import {useHandleScroll} from '../pages/HomePage/HomePageSidebar/useHandleScroll';
import {Analytics} from '../services/analytics';
import {
  getPrimarySub,
  orgPrimarySubUnitPrice,
  useAccountPrivileges,
} from '../util/accounts/pricing';
import {getErrMsgFromError, propagateErrorsContext} from '../util/errors';
import {useStatsigGateBulkUserInvitations} from '../util/rampFeatureFlags';
import {BulkInviteForm} from './BulkInviteForm';
import {ResizableDrawer} from './common/ResizableDrawer/ResizableDrawer';
import {useGetRoleOptions} from './MembersTable/TeamMembersTable/useGetRoleOptions';
import {TeamMemberRole} from './MembersTable/TeamMembersTable/utils';
import {RoleOption} from './MembersTable/TeamOrOrganizationRoleOption';
import {
  getInviteRoleOptions,
  OrganizationMemberRole,
} from './MembersTable/util';
import {AccountSelectorContext} from './Search/SearchNav/AccountSelectorContextProvider';
import {SingleInviteForm} from './SingleInviteForm';

type AddUserOrganization = Pick<
  NonNullable<OrganizationMembersTableQuery['organization']>,
  'name' | 'id' | 'seatAvailability' | 'subscriptions'
> & {
  teams: Array<Pick<Entity, 'id' | 'name' | 'memberCount'>>;
};

type AddUserPropsBase = {
  open: boolean;
  toggleTeamDrawer: () => void;
  analyticsLocation: string;
  refetch?: () => void;
  organization: AddUserOrganization;
  roleOptionsList: RoleOption[];
  isWeaveAsAServiceUser: boolean;
  scrolled: boolean;
};

type TeamInviteProps =
  | {isTeamInvite: true; existingTeamName: string}
  | {isTeamInvite: false};

type AddExistingUserProps =
  | {isAddingExistingUser: true; existingOrgMember: string}
  | {isAddingExistingUser: false};

type AddUserProps = AddUserPropsBase & TeamInviteProps & AddExistingUserProps;

export const AddUserDrawerComp = ({
  open,
  toggleTeamDrawer,
  analyticsLocation,
  refetch,
  organization,
  roleOptionsList,
  isTeamInvite,
  isAddingExistingUser,
  isWeaveAsAServiceUser,
  scrolled,
  ...conditionalProps
}: AddUserProps) => {
  const [width, setWidth] = useState(600);
  const onSetWidth = (newWidth: number) => {
    setWidth(newWidth);
  };
  const onClose = (e: React.MouseEvent) => {
    clearInputState();
    toggleTeamDrawer();
  };

  const teamOptions = getTeamOptions(organization);

  const enabledRoleOptions = isTeamInvite
    ? roleOptionsList.filter(
        roleOption =>
          (!roleOption.isPremiumOption ||
            !roleOption.isPremiumFeatureDisabled) &&
          roleOption.viewerCanInviteOtherAsRole
      )
    : roleOptionsList;

  const defaultRole =
    (enabledRoleOptions.find(
      roleOption =>
        !roleOption.isPremiumOption || !roleOption.isPremiumFeatureDisabled
    )?.value as TeamMemberRole | OrganizationMemberRole) ??
    (isTeamInvite
      ? (TeamMemberRole.MEMBER as TeamMemberRole)
      : (OrganizationMemberRole.ADMIN as OrganizationMemberRole));

  const existingTeamName = isTeamInvite
    ? (conditionalProps as {existingTeamName: string}).existingTeamName
    : '';
  const existingOrgMember = isAddingExistingUser
    ? (conditionalProps as {existingOrgMember: string}).existingOrgMember
    : '';

  const [emailOrUsername, setEmailOrUsername] = useState(existingOrgMember);
  const [teams, setTeams] = useState<string[]>(
    isTeamInvite ? [existingTeamName] : []
  );
  const [role, setRole] = useState(defaultRole);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [showContactSalesOnError, setShowContactSalesOnError] = useState(false);

  const clearInputState = useCallback(() => {
    setEmailOrUsername('');
    setTeams(teams);
    setRole(defaultRole);
    setErrorMsg(null);
    setShowContactSalesOnError(false);
  }, [defaultRole, teams]);

  const {addMemberToOrganization, addMemberToTeam} = useAddMember({
    organization,
    teams,
    emailOrUsername,
    role,
    analyticsLocation,
    existingTeamName,
    refetch,
    toggleTeamDrawer,
    setErrorMsg,
    setShowContactSalesOnError,
    clearInputState,
  });

  const isBulkInvite = useStatsigGateBulkUserInvitations();

  const onAddMemberClick = async () => {
    const hasUserSelected = emailOrUsername !== '';
    if (!hasUserSelected || teams.length === 0) {
      setErrorMsg(`Please fill out all the fields to add a member.`);
      return;
    }

    if (isTeamInvite) {
      await addMemberToTeam();
    } else {
      await addMemberToOrganization();
    }
  };

  // Get the mutation for bulk invites
  const [addUserToOrganizationMutation] = useAddUserToOrganizationMutation({
    context: propagateErrorsContext(),
  });

  const onBulkInviteSubmit = async (formData: {
    users: string[];
    teams: string[];
    role: string;
  }) => {
    const {users, teams: selectedTeams, role: selectedRole} = formData;

    if (
      users.length === 0 ||
      selectedTeams.length === 0 ||
      selectedRole === ''
    ) {
      // Form validation should prevent this, but just in case
      return;
    }

    let successCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    Analytics.track('Bulk Add Organization Members Clicked', {
      entityNames: selectedTeams,
      organizationName: organization?.name,
      location: analyticsLocation,
      userCount: users.length,
    });

    // Process each user individually using the mutation directly
    for (const user of users) {
      try {
        const addUserResult = await addUserToOrganizationMutation({
          variables: {
            userName: user.trim(),
            teams: selectedTeams,
            organizationId: organization?.id || '',
            userOrgRole: selectedRole,
          },
        });

        if (addUserResult.errors) {
          const errMsg = addUserResult.errors
            .map((e: any) => e.message)
            .join(', ');
          errors.push(`${user}: ${errMsg}`);
          errorCount++;
        } else if (
          addUserResult.data &&
          !addUserResult.data.addUserToOrganization?.success
        ) {
          errors.push(`${user}: Error adding user - please contact support`);
          errorCount++;
        } else if (
          addUserResult.data &&
          addUserResult.data.addUserToOrganization?.success
        ) {
          successCount++;
          Analytics.track('New Org Member Added', {
            organizationName: organization?.name,
            entityNames: selectedTeams,
            location: analyticsLocation,
            role: selectedRole,
            organizationId: organization?.id,
            usernameOrEmail: user,
          });
        }
      } catch (error) {
        errorCount++;
        const errMsg = getErrMsgFromError(error);
        errors.push(`${user}: ${errMsg}`);
      }
    }

    // Show results to user
    if (successCount > 0 && errorCount === 0) {
      toast(
        `Successfully invited ${successCount} user${successCount > 1 ? 's' : ''}.`
      );
      clearInputState();
      toggleTeamDrawer();
      refetch?.();
    } else if (successCount > 0 && errorCount > 0) {
      toast(
        `Successfully invited ${successCount} user${successCount > 1 ? 's' : ''}. ${errorCount} invitation${errorCount > 1 ? 's' : ''} failed.`
      );
    } else {
      toast(`Failed to invite ${errorCount} user${errorCount > 1 ? 's' : ''}.`);

      // Track bulk invite errors
      Analytics.track('Bulk Invite Error Viewed', {
        organizationName: organization?.name,
        entityNames: selectedTeams,
        location: analyticsLocation,
        error: errors.join('; '),
        userCount: users.length,
      });
    }
  };

  // TODO(sammy): simplify drawer title.
  const title = () => {
    if (isAddingExistingUser && existingOrgMember) {
      // Context: Organization member action menu - adding existing user to teams
      return 'Add user to a team';
    } else if (isTeamInvite && existingTeamName) {
      // Context: Team members table - inviting new users to specific team
      return 'Add a user to a team';
    }

    // (!isTeamInvite && !isAddingExistingUser)
    // Context: Organization members table - inviting new users to org
    return isBulkInvite ? 'Invite new users' : 'Invite new user';
  };

  const padding = scrolled ? '0px 20px 20px 20px' : '60px 20px 20px 20px';

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <ResizableDrawer
        startWidth={width}
        minWidth={400}
        onSetWidth={onSetWidth}>
        <Tailwind>
          <div className="flex h-screen flex-col" style={{padding}}>
            <div className="flex h-60 items-center flex-shrink-0">
              <div className="font-source-sans-pro text-2xl font-semibold leading-10">
                {title()}
              </div>
              <div className="flex-shrink flex-grow basis-auto text-right">
                <Button
                  onClick={onClose}
                  icon="close"
                  size="large"
                  variant="ghost"
                />
              </div>
            </div>
            <div className="-ml-20 -mr-20 h-px bg-moon-250 flex-shrink-0" />
            <div className="flex-1 flex flex-col min-h-0">
              {isBulkInvite && !isTeamInvite && !isAddingExistingUser ? (
                <BulkInviteForm
                  onSubmit={onBulkInviteSubmit}
                  teamOptions={organization.teams.map(team => ({
                    label: team.name,
                    value: team.name,
                    userCount: team.memberCount,
                  }))}
                  roleOptions={roleOptionsList}
                  availableSeats={organization.seatAvailability?.seats ?? 0}
                />
              ) : (
                <SingleInviteForm
                  emailOrUsername={emailOrUsername}
                  setEmailOrUsername={setEmailOrUsername}
                  teams={teams}
                  setTeams={setTeams}
                  role={role}
                  setRole={setRole}
                  errorMsg={errorMsg}
                  setErrorMsg={setErrorMsg}
                  showContactSalesOnError={showContactSalesOnError}
                  teamOptions={teamOptions}
                  enabledRoleOptions={enabledRoleOptions}
                  isTeamInvite={isTeamInvite}
                  isAddingExistingUser={isAddingExistingUser}
                  existingTeamName={existingTeamName}
                  existingOrgMember={existingOrgMember}
                  organizationName={organization?.name || ''}
                  analyticsLocation={analyticsLocation}
                  onSubmit={onAddMemberClick}
                />
              )}
            </div>
          </div>
        </Tailwind>
      </ResizableDrawer>
    </Drawer>
  );
};

function getTeamOptions(
  organization: Pick<AddUserOrganization, 'teams'> | undefined
): Option[] {
  const teamNames = (organization?.teams ?? [])
    .map(team => team?.name)
    .filter(isNotNullOrUndefined);
  const sortedTeamNames = _.sortBy(teamNames);

  return sortedTeamNames.map(teamName => {
    return {text: teamName, value: teamName};
  });
}

type UseAddMemberProps = {
  organization: AddUserOrganization;
  teams: string[];
  emailOrUsername: string;
  role: OrganizationMemberRole | TeamMemberRole;
  analyticsLocation: string;
  existingTeamName: string;
  refetch: (() => void) | undefined;
  toggleTeamDrawer: () => void;
  setErrorMsg: React.Dispatch<React.SetStateAction<string | null>>;
  setShowContactSalesOnError: React.Dispatch<React.SetStateAction<boolean>>;
  clearInputState: () => void;
};

const useAddMember = ({
  organization,
  teams,
  emailOrUsername,
  role,
  analyticsLocation,
  existingTeamName,
  refetch,
  toggleTeamDrawer,
  setErrorMsg,
  setShowContactSalesOnError,
  clearInputState,
}: UseAddMemberProps) => {
  const [addUserToOrganizationMutation] = useAddUserToOrganizationMutation({
    context: propagateErrorsContext(),
  });

  const [createInvite] = useCreateInviteMutation({
    context: propagateErrorsContext(),
  });

  const primarySub = getPrimarySub(organization);
  const planUnitPrice = orgPrimarySubUnitPrice(organization);
  const availableSeatsToPurchase = primarySub?.availableSeatsToPurchase ?? 0;
  const canPurchaseMoreSeat =
    planUnitPrice != null && availableSeatsToPurchase > 0;

  const addMemberToOrganization = async () => {
    if (!role) {
      return;
    }

    Analytics.track('Add Organization Member Clicked', {
      entityNames: teams,
      organizationName: organization?.name,
      location: analyticsLocation,
    });

    try {
      const addUserResult = await addUserToOrganizationMutation({
        variables: {
          userName: emailOrUsername.trim(),
          teams,
          organizationId: organization?.id || '',
          userOrgRole: role,
        },
      });

      if (addUserResult.errors) {
        const errMsg = addUserResult.errors.map(e => e.message).join(', ');
        setErrorMsg(`Error adding user: ${errMsg}`);
        Analytics.track('Invite Error Viewed', {
          organizationName: organization?.name,
          entityNames: teams,
          location: analyticsLocation,
          error: errMsg,
        });
      } else if (
        addUserResult.data &&
        !addUserResult.data.addUserToOrganization?.success
      ) {
        const errMessage = `Error adding user: please contact support`;
        setErrorMsg(errMessage);
        Analytics.track('Invite Error Viewed', {
          organizationName: organization?.name,
          entityNames: teams,
          location: analyticsLocation,
          error: errMessage,
        });
      } else if (
        addUserResult.data &&
        addUserResult.data.addUserToOrganization?.success
      ) {
        clearInputState();
        Analytics.track('New Org Member Added', {
          organizationName: organization?.name,
          entityNames: teams,
          location: analyticsLocation,
          role,
          organizationId: organization?.id,
          usernameOrEmail: emailOrUsername,
        });
        toggleTeamDrawer();
        refetch?.();
        toast('Successfully added user to organization.');
      }
    } catch (err) {
      const errMsg = getErrMsgFromError(err);
      setErrorMsg(
        `Error adding user to organization${errMsg ? `: ${errMsg}` : ``}`
      );
      Analytics.track('Invite Error Viewed', {
        organizationName: organization?.name,
        entityNames: teams,
        location: analyticsLocation,
        error: errMsg,
      });
    }
  };

  const addMemberToTeam = async () => {
    if (!existingTeamName) {
      return;
    }

    Analytics.track('Invite Submitted', {
      entityName: existingTeamName,
      organizationName: organization?.name,
      location: analyticsLocation,
    });

    const doCreateInvite = async () => {
      try {
        const createInviteResult = await createInvite({
          variables: {
            entityName: existingTeamName,
            role,
            [String.isValidEmail(emailOrUsername) ? 'email' : 'username']:
              emailOrUsername.trim(),
          },
        });

        if (createInviteResult.errors != null) {
          setErrorMsg(createInviteResult.errors.map(e => e.message).join(', '));
          clearInputState();
          return;
        }

        const inviteData = createInviteResult.data?.createInvite;

        let remainingSeats = false;
        if (inviteData) {
          const {remainingSeats: seats, remainingViewOnlySeats: viewSeats} =
            inviteData;
          remainingSeats =
            (seats != null && seats > 0) ||
            (viewSeats != null && viewSeats > 0);
        }

        if (!createInviteResult.data?.createInvite?.success) {
          setErrorMsg(`Error adding user to org. Please contact support`);
          setShowContactSalesOnError(!canPurchaseMoreSeat);
        } else {
          clearInputState();
          toggleTeamDrawer();
          refetch?.();
          toast('Successfully added user to team.');
        }
      } catch (err) {
        const errMsg = getErrMsgFromError(err);
        setErrorMsg(errMsg);
      }
    };

    await doCreateInvite();
  };

  return {addMemberToOrganization, addMemberToTeam};
};

type AddUserButtonProps = {
  disabled: boolean;
  refetch?: () => void;
  existingTeamName?: string | null;
  analyticsLocation: string;
  organization: NonNullable<OrganizationSubscriptionInfoQuery['organization']>;
};

export const AddUserButton = ({
  disabled,
  refetch,
  existingTeamName,
  analyticsLocation,
  organization,
}: AddUserButtonProps) => {
  const {scrolled} = useHandleScroll();
  const {selectedAccount} = useContext(AccountSelectorContext);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const toggleAddUserDrawer = useCallback(() => {
    setIsAddingUser(!isAddingUser);
  }, [isAddingUser]);

  const isTeamLevelInvite = existingTeamName != null;

  const isViewOnlyAvailable =
    (organization?.seatAvailability?.viewOnlySeats ?? 0) > 0;
  const {privileges} = useAccountPrivileges(selectedAccount);

  const orgLevelInviteRoleOptions = getInviteRoleOptions(
    isViewOnlyAvailable,
    privileges.roleManagementEnabled
  );

  const {roleOptions: teamLevelInviteRoleOptions} = useGetRoleOptions(
    existingTeamName ?? ''
  );

  if (organization === undefined) {
    return <Loading></Loading>;
  }

  const roleOptions = isTeamLevelInvite
    ? teamLevelInviteRoleOptions
    : orgLevelInviteRoleOptions;

  return (
    <>
      <Button
        className="font-normal"
        data-test="add-team-invite-button"
        startIcon="add-new"
        variant={'ghost'}
        disabled={disabled}
        tooltip={disabled ? 'Buy more seats in order to invite new users' : ''}
        onClick={toggleAddUserDrawer}>
        Invite new user
      </Button>
      <AddUserDrawerComp
        open={isAddingUser}
        scrolled={scrolled}
        toggleTeamDrawer={toggleAddUserDrawer}
        analyticsLocation={analyticsLocation}
        refetch={refetch}
        organization={organization}
        existingTeamName={
          isTeamLevelInvite && existingTeamName ? existingTeamName : ''
        }
        roleOptionsList={roleOptions}
        isTeamInvite={isTeamLevelInvite}
        isAddingExistingUser={false}
        isWeaveAsAServiceUser
      />
    </>
  );
};

export const AddUserDrawer = memo(AddUserButton);
